import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import type { CrosswordPuzzle, CrosswordWord } from '../../types'
import { buildCellMap } from '../../lib/crossword/generator'

// ─── Exposed handle ───────────────────────────────────────────────────────────

export interface CrosswordGridHandle {
  /** Type a single character into the currently selected cell, then advance. */
  typeChar: (char: string) => void
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface Selection {
  word:    CrosswordWord
  cellIdx: number   // index within the word (0 = first letter)
}

interface CrosswordGridProps {
  puzzle:     CrosswordPuzzle
  checked:    boolean
  onComplete: () => void
  onSelectionChange?: (word: CrosswordWord | null) => void
}

// ─── Component ────────────────────────────────────────────────────────────────

const CrosswordGrid = forwardRef<CrosswordGridHandle, CrosswordGridProps>(function CrosswordGrid(
  { puzzle, checked, onComplete, onSelectionChange }: CrosswordGridProps,
  ref,
) {
  const { words, rows, cols } = puzzle

  // user letters: Map keyed "row,col" → uppercase letter or ''
  const [letters, setLetters] = useState<Map<string, string>>(new Map())
  const [selection, setSelection] = useState<Selection | null>(null)
  const inputRefs = useRef<Map<string, HTMLInputElement | null>>(new Map())
  const cellMap = buildCellMap(puzzle)

  // ── Expose typeChar to parent via ref ─────────────────────────────────────
  useImperativeHandle(ref, () => ({
    typeChar(char: string) {
      if (!selection) return
      const upper = char.toUpperCase()
      const { word, cellIdx } = selection
      const dr = word.direction === 'down' ? 1 : 0
      const dc = word.direction === 'across' ? 1 : 0
      const r = word.row + dr * cellIdx
      const c = word.col + dc * cellIdx
      const key = `${r},${c}`
      setLetters((prev) => { const m = new Map(prev); m.set(key, upper); return m })
      if (cellIdx < word.word.length - 1) {
        const nextIdx = cellIdx + 1
        const nr = word.row + dr * nextIdx
        const nc = word.col + dc * nextIdx
        setSelection({ word, cellIdx: nextIdx })
        inputRefs.current.get(`${nr},${nc}`)?.focus()
      }
    },
  }), [selection])  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Notify parent of selection changes ────────────────────────────────────
  useEffect(() => {
    onSelectionChange?.(selection?.word ?? null)
  }, [selection, onSelectionChange])

  // ── Check completion ──────────────────────────────────────────────────────
  useEffect(() => {
    const allFilled = words.every((w) => {
      const dr = w.direction === 'down' ? 1 : 0
      const dc = w.direction === 'across' ? 1 : 0
      return Array.from({ length: w.word.length }, (_, i) => {
        const key = `${w.row + dr * i},${w.col + dc * i}`
        return letters.get(key) === w.word[i]
      }).every(Boolean)
    })
    if (allFilled && letters.size > 0) onComplete()
  }, [letters, words, onComplete])

  // ── Cell state helpers ────────────────────────────────────────────────────

  function isSelectedWord(r: number, c: number): boolean {
    if (!selection) return false
    const { word } = selection
    const dr = word.direction === 'down' ? 1 : 0
    const dc = word.direction === 'across' ? 1 : 0
    for (let i = 0; i < word.word.length; i++) {
      if (word.row + dr * i === r && word.col + dc * i === c) return true
    }
    return false
  }

  function isActiveCursor(r: number, c: number): boolean {
    if (!selection) return false
    const { word, cellIdx } = selection
    const dr = word.direction === 'down' ? 1 : 0
    const dc = word.direction === 'across' ? 1 : 0
    return word.row + dr * cellIdx === r && word.col + dc * cellIdx === c
  }

  function cellStatus(r: number, c: number, info: ReturnType<typeof cellMap.get>): 'correct' | 'wrong' | 'empty' {
    if (!checked || !info) return 'empty'
    const userLetter = letters.get(`${r},${c}`) ?? ''
    if (!userLetter) return 'empty'
    // Check against any word passing through this cell
    const word = info.across ?? info.down
    if (!word) return 'empty'
    const dr = word.direction === 'down' ? 1 : 0
    const dc = word.direction === 'across' ? 1 : 0
    for (let i = 0; i < word.word.length; i++) {
      if (word.row + dr * i === r && word.col + dc * i === c) {
        return userLetter === word.word[i] ? 'correct' : 'wrong'
      }
    }
    return 'empty'
  }

  // ── Focus management ──────────────────────────────────────────────────────

  function focusCell(r: number, c: number) {
    inputRefs.current.get(`${r},${c}`)?.focus()
  }

  // ── Click handler ─────────────────────────────────────────────────────────

  function handleCellClick(r: number, c: number) {
    const info = cellMap.get(`${r},${c}`)
    if (!info) return

    const clickedAcross = info.across
    const clickedDown   = info.down

    if (!clickedAcross && !clickedDown) return

    // Toggle direction if clicking the same cell again and both directions exist
    if (selection && isActiveCursor(r, c) && clickedAcross && clickedDown) {
      const newDir = selection.word.direction === 'across' ? 'down' : 'across'
      const newWord = newDir === 'across' ? clickedAcross : clickedDown
      const dr = newWord.direction === 'down' ? 1 : 0
      const dc = newWord.direction === 'across' ? 1 : 0
      let idx = 0
      for (let i = 0; i < newWord.word.length; i++) {
        if (newWord.row + dr * i === r && newWord.col + dc * i === c) { idx = i; break }
      }
      setSelection({ word: newWord, cellIdx: idx })
      return
    }

    // Prefer current direction if possible, else pick what's available
    let word: CrosswordWord
    if (selection && clickedAcross && selection.word.direction === 'across') {
      word = clickedAcross
    } else if (selection && clickedDown && selection.word.direction === 'down') {
      word = clickedDown
    } else {
      word = clickedAcross ?? clickedDown!
    }

    const dr = word.direction === 'down' ? 1 : 0
    const dc = word.direction === 'across' ? 1 : 0
    let idx = 0
    for (let i = 0; i < word.word.length; i++) {
      if (word.row + dr * i === r && word.col + dc * i === c) { idx = i; break }
    }
    setSelection({ word, cellIdx: idx })
    focusCell(r, c)
  }

  // ── Keyboard handler ──────────────────────────────────────────────────────

  function handleKeyDown(e: React.KeyboardEvent, r: number, c: number) {
    if (!selection) return
    const { word, cellIdx } = selection
    const dr = word.direction === 'down' ? 1 : 0
    const dc = word.direction === 'across' ? 1 : 0

    if (e.key === 'Backspace') {
      e.preventDefault()
      const key = `${r},${c}`
      const current = letters.get(key) ?? ''
      if (current) {
        setLetters((prev) => { const m = new Map(prev); m.set(key, ''); return m })
      } else if (cellIdx > 0) {
        const prevIdx = cellIdx - 1
        const pr = word.row + dr * prevIdx
        const pc = word.col + dc * prevIdx
        setLetters((prev) => { const m = new Map(prev); m.set(`${pr},${pc}`, ''); return m })
        setSelection({ word, cellIdx: prevIdx })
        focusCell(pr, pc)
      }
      return
    }

    if (e.key === 'Tab') {
      e.preventDefault()
      const allWords = [...words].sort((a, b) => a.number !== b.number ? a.number - b.number : (a.direction === 'across' ? -1 : 1))
      const idx = allWords.indexOf(word)
      const nextWord = allWords[(idx + (e.shiftKey ? -1 + allWords.length : 1)) % allWords.length]
      setSelection({ word: nextWord, cellIdx: 0 })
      focusCell(nextWord.row, nextWord.col)
      return
    }

    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault()
      const [deltaR, deltaC] = e.key === 'ArrowRight' ? [0,1] : e.key === 'ArrowLeft' ? [0,-1] : e.key === 'ArrowUp' ? [-1,0] : [1,0]
      const nr = r + deltaR, nc = c + deltaC
      const nextInfo = cellMap.get(`${nr},${nc}`)
      if (nextInfo) {
        const nextWord = nextInfo.across ?? nextInfo.down!
        let ni = 0
        const ndr = nextWord.direction === 'down' ? 1 : 0
        const ndc = nextWord.direction === 'across' ? 1 : 0
        for (let i = 0; i < nextWord.word.length; i++) {
          if (nextWord.row + ndr * i === nr && nextWord.col + ndc * i === nc) { ni = i; break }
        }
        setSelection({ word: nextWord, cellIdx: ni })
        focusCell(nr, nc)
      }
      return
    }
  }

  function handleInput(e: React.FormEvent<HTMLInputElement>, r: number, c: number) {
    if (!selection) return
    const raw = (e.currentTarget.value).toUpperCase().replace(/[^A-ZČŠŽÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖÙÚÛÜÝ]/g, '')
    const letter = raw.slice(-1)  // take last char (new input)

    const key = `${r},${c}`
    setLetters((prev) => { const m = new Map(prev); m.set(key, letter); return m })

    // Advance to next cell in word
    const { word, cellIdx } = selection
    const dr = word.direction === 'down' ? 1 : 0
    const dc = word.direction === 'across' ? 1 : 0
    if (cellIdx < word.word.length - 1) {
      const nextIdx = cellIdx + 1
      const nr = word.row + dr * nextIdx
      const nc = word.col + dc * nextIdx
      setSelection({ word, cellIdx: nextIdx })
      focusCell(nr, nc)
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  // Cell size: fit grid within container, min 24px
  const cellSize = `clamp(24px, calc((min(100vw - 80px, 560px)) / ${cols}), 40px)`

  return (
    <div
      className="select-none"
      style={{
        display: 'inline-grid',
        gridTemplateColumns: `repeat(${cols}, ${cellSize})`,
        gridTemplateRows:    `repeat(${rows}, ${cellSize})`,
        gap: 1,
        backgroundColor: '#888',
        border: '2px solid #444',
        borderRadius: 4,
      }}
    >
      {Array.from({ length: rows }, (_, r) =>
        Array.from({ length: cols }, (_, c) => {
          const key  = `${r},${c}`
          const info = cellMap.get(key)

          if (!info) {
            // Black cell
            return (
              <div
                key={key}
                style={{ width: cellSize, height: cellSize, backgroundColor: '#333' }}
              />
            )
          }

          const isSelected   = isSelectedWord(r, c)
          const isCursor     = isActiveCursor(r, c)
          const status       = cellStatus(r, c, info)
          const userLetter   = letters.get(key) ?? ''

          const bg = isCursor
            ? '#4ECDC4'
            : isSelected
            ? '#B2F0EC'
            : status === 'correct'
            ? '#BBEFCE'
            : status === 'wrong'
            ? '#FFD5D5'
            : 'white'

          return (
            <div
              key={key}
              onClick={() => handleCellClick(r, c)}
              style={{ width: cellSize, height: cellSize, backgroundColor: bg, position: 'relative', cursor: 'pointer' }}
            >
              {/* Clue number */}
              {info.number !== undefined && (
                <span style={{
                  position: 'absolute',
                  top: 1, left: 2,
                  fontSize: 'clamp(7px, 1.8vw, 10px)',
                  lineHeight: 1,
                  fontWeight: 900,
                  color: '#444',
                  fontFamily: 'Nunito, sans-serif',
                  pointerEvents: 'none',
                  zIndex: 1,
                }}>
                  {info.number}
                </span>
              )}

              {/* Letter input */}
              <input
                ref={(el) => inputRefs.current.set(key, el)}
                type="text"
                inputMode="text"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                value={userLetter}
                onInput={(e) => handleInput(e, r, c)}
                onKeyDown={(e) => handleKeyDown(e, r, c)}
                onFocus={() => handleCellClick(r, c)}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  fontFamily: 'Nunito, sans-serif',
                  fontWeight: 900,
                  fontSize: 'clamp(12px, 3.5vw, 20px)',
                  color: '#222',
                  cursor: 'pointer',
                  caretColor: 'transparent',
                  paddingTop: info.number !== undefined ? '30%' : '0',
                }}
                maxLength={2}  // allow 2 so we can detect new char
                aria-label={`Row ${r + 1} column ${c + 1}`}
              />
            </div>
          )
        })
      )}
    </div>
  )
})

export default CrosswordGrid
